import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchAllBrands } from '../../redux/actions/brandActions';
import { BASE_URL } from '../../constant';
import styles from '../../sass/brands.module.scss';

const NAV_ITEMS = ['ALL', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''), '#'];

const getBrandBucket = (name) => {
  if (typeof name !== 'string') {
    return '#';
  }

  const trimmed = name.trim();
  if (!trimmed) {
    return '#';
  }

  const firstChar = trimmed.charAt(0).toUpperCase();
  return /^[A-Z]$/.test(firstChar) ? firstChar : '#';
};

const AllBrands = () => {
  const dispatch = useDispatch();
  const { allBrands, loader, errors } = useSelector((state) => state.brands);
  const [activeLetter, setActiveLetter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  const hasBrands = Array.isArray(allBrands) && allBrands.length > 0;

  useEffect(() => {
    if (!hasBrands) {
      dispatch(fetchAllBrands());
    }
  }, [dispatch, hasBrands]);

  const sortedBrands = useMemo(() => {
    if (!Array.isArray(allBrands)) {
      return [];
    }

    return [...allBrands]
      .filter((brand) => brand && typeof brand.name === 'string')
      .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
  }, [allBrands]);

  const groupedBrands = useMemo(() => {
    const map = new Map();

    NAV_ITEMS.slice(1).forEach((letter) => {
      map.set(letter, []);
    });

    sortedBrands.forEach((brand) => {
      const bucket = getBrandBucket(brand.name);
      const existing = map.get(bucket) || [];
      existing.push(brand);
      map.set(bucket, existing);
    });

    return Array.from(map.entries())
      .filter(([, brands]) => brands.length > 0)
      .map(([letter, brands]) => ({
        letter,
        brands,
      }));
  }, [sortedBrands]);

  const filteredGroups = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (query) {
      return groupedBrands
        .map(({ letter, brands }) => {
          const matches = brands.filter((brand) => brand.name.toLowerCase().includes(query));
          return matches.length ? { letter, brands: matches } : null;
        })
        .filter(Boolean);
    }

    if (activeLetter === 'ALL') {
      return groupedBrands;
    }

    return groupedBrands.filter(({ letter }) => letter === activeLetter);
  }, [groupedBrands, searchTerm, activeLetter]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (activeLetter !== 'ALL') {
      setActiveLetter('ALL');
    }
  };

  const renderState = () => {
    if (loader && !hasBrands) {
      return (
        <div className={styles.stateCard}>
          <span className={styles.loaderSpinner} aria-hidden="true" />
          <span className={styles.stateMessage}>Loading brands...</span>
        </div>
      );
    }

    if (errors && !hasBrands) {
      return (
        <div className={styles.stateCard}>
          <h2 className={`${styles.stateTitle} ${styles.stateTitleError}`}>Unable to fetch brands</h2>
          <p className={styles.stateDescription}>
            {typeof errors === 'string' ? errors : JSON.stringify(errors)}
          </p>
        </div>
      );
    }

    if (!loader && filteredGroups.length === 0) {
      return (
        <div className={styles.stateCard}>
          <h2 className={styles.stateTitle}>
            {hasBrands ? 'No brands match your search' : 'Brands will appear here soon'}
          </h2>
          <p className={styles.stateDescription}>
            {hasBrands
              ? 'Try adjusting the search or picking a different alphabet filter.'
              : 'Once new brands are added to the catalogue, you will see them grouped alphabetically here.'}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles.brandsPage}>
      <div className={styles.pageContainer}>
        <header className={styles.header}>
          <h1 className={styles.title}>Explore Brands</h1>
          <p className={styles.subtitle}>
            Browse the complete catalogue alphabetically or find a brand instantly by typing its name.
          </p>
        </header>

        <div className={styles.alphabetBar}>
          <nav className={styles.alphabetNav} aria-label="Brand alphabet navigation">
            {NAV_ITEMS.map((item) => (
              <button
                key={item}
                type="button"
                className={item === activeLetter ? `${styles.alphabetButton} ${styles.isActive}` : styles.alphabetButton}
                onClick={() => setActiveLetter(item)}
              >
                {item}
              </button>
            ))}
          </nav>
          <div className={styles.searchWrapper}>
            <input
              type="search"
              value={searchTerm}
              onChange={handleSearchChange}
              className={styles.searchInput}
              placeholder="Search brands..."
              aria-label="Search brands"
            />
          </div>
        </div>

        {renderState()}

        {filteredGroups.length > 0 && (
          <div className={styles.sectionsWrapper}>
            {filteredGroups.map(({ letter, brands }) => (
              <section key={letter} className={styles.brandSection} aria-labelledby={`brand-group-${letter}`}>
                <div className={styles.sectionHeader}>
                  <h2 id={`brand-group-${letter}`} className={styles.sectionTitle}>
                    {letter}
                  </h2>
                  <div className={styles.sectionDivider} />
                </div>
                <div className={styles.brandGrid}>
                  {brands.map((brand) => {
                    const brandName = brand.name?.trim() || 'Unnamed Brand';
                    const initials = brandName.charAt(0).toUpperCase();
                    const logoSrc = brand.thumbnail ? `${BASE_URL}${brand.thumbnail}` : null;

                    return (
                      <Link
                        to={`/brand/${brand.slug}`}
                        key={brand.id}
                        className={styles.brandCard}
                        aria-label={`View products from ${brandName}`}
                      >
                        <div className={styles.brandLogo}>
                          {logoSrc && (
                            <img
                              src={logoSrc}
                              alt={brandName}
                              className={styles.brandImage}
                              onError={(event) => {
                                const imageElement = event.currentTarget;
                                imageElement.classList.add(styles.isHidden);
                                const fallback = imageElement.nextSibling;
                                if (fallback) {
                                  fallback.classList.remove(styles.isFallbackHidden);
                                }
                              }}
                            />
                          )}
                          <span
                            className={logoSrc ? `${styles.brandInitial} ${styles.isFallbackHidden}` : styles.brandInitial}
                            aria-hidden={logoSrc ? 'true' : 'false'}
                          >
                            {initials}
                          </span>
                        </div>
                        <span className={styles.brandName}>{brandName}</span>
                      </Link>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllBrands;
